import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect } from "react";
import { Button, Modal, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import "./modal.scss";

/**
 * Modal component with flexible button configuration
 * 
 * @param {Object} props Component props
 * @param {boolean} props.show Controls modal visibility
 * @param {Function} props.handleClose Function to close the modal
 * @param {Function} props.handleSubmit Function for the primary action button
 * @param {string} props.title Modal title text
 * @param {ReactNode} props.body Modal body content
 * @param {boolean} props.centered Whether to center the modal vertically
 * @param {string} props.saveText Translation key for the save button text
 * @param {ReactNode} props.footer Custom footer content
 * @param {string} props.customClassName Additional CSS class for the modal
 * @param {string} props.titleHelpText Tooltip text for the info icon next to title
 * @param {boolean} props.hideCloseButton Whether to hide the close/cancel button
 * @param {boolean} props.hideSaveButton Whether to hide the save/submit button
 * @param {string} props.backdropClassName Additional CSS class for the modal backdrop
 * @param {Object} props.backdropStyle Style object for the modal backdrop
 * @param {Array} props.customButtons Array of custom button configurations
 * @param {string} props.size Bootstrap modal size: "sm" | "lg" | "xl" | "" (default/medium)
 * @param {boolean} props.disabled In-flight state - locks every control (Save, Cancel, X)
 *   and shows a spinner on the Save button. Use for "we're currently saving / deleting".
 * @param {boolean} props.submitDisabled Save-button validity only - greys out the Save
 *   button without showing a spinner and without affecting Cancel or the X close button.
 *   Use for "the form isn't ready to submit yet" (e.g. no selection, validation errors).
 */
function ModalAtom(props) {
	const {
		show,
		handleClose,
		handleSubmit,
		title,
		body,
		centered = true,
		saveText,
		footer,
		customClassName = "",
		titleHelpText = "",
		hideCloseButton = false,
		hideSaveButton = false,
		disabled = false,
		submitDisabled = false,
		backdropClassName = "",
		backdropStyle = {},
		customButtons = [],
		size = "",
		...restProps
	} = props;

	const { t } = useTranslation();

	// Apply backdrop style if provided
	useEffect(() => {
		if (show && Object.keys(backdropStyle).length > 0) {
			// Find the modal backdrop element and apply style
			setTimeout(() => {
				const backdrop = document.querySelector('.modal-backdrop');
				if (backdrop) {
					Object.entries(backdropStyle).forEach(([key, value]) => {
						backdrop.style[key] = value;
					});
				}
			}, 10); // Short delay to ensure the backdrop is in the DOM
		}
	}, [show, backdropStyle]);

	// Render a button with optional tooltip
	const renderButton = (buttonProps) => {
		const {
			text,
			onClick = () => { },
			variant = "primary",
			tooltip = "",
			className = "",
			disabled = false,
		} = buttonProps;

		const button = (
			<Button
				variant={variant}
				onClick={onClick}
				className={className}
				disabled={disabled}
			>
				{text}
			</Button>
		);

		if (tooltip) {
			return (
				<OverlayTrigger
					key={text}
					overlay={<Tooltip>{tooltip}</Tooltip>}
				>
					<span className="d-inline-block">{button}</span>
				</OverlayTrigger>
			);
		}

		return button;
	};

	return (
		<Modal
			show={show}
			onHide={handleClose}
			centered={centered}
			dialogClassName={`${customClassName ? customClassName : ''} modal-center modal-fixed-height`}
			backdrop="static"
			backdropClassName={backdropClassName}
			keyboard={false}
			size={size}
			{...restProps}
		>
			<Modal.Header>
				<Modal.Title>
					{title}&nbsp;
					{titleHelpText && (
						<OverlayTrigger
							overlay={<Tooltip>{titleHelpText}</Tooltip>}
						>
							<FontAwesomeIcon icon={faCircleInfo} />
						</OverlayTrigger>
					)}
				</Modal.Title>
				{!hideCloseButton && (
					<button
						type="button"
						className="modal-header-close"
						onClick={handleClose}
						aria-label={t("common.cancel")}
						disabled={disabled}
					>
						&times;
					</button>
				)}
			</Modal.Header>
			<Modal.Body className="modal-body-scrollable">{body}</Modal.Body>
			{(customButtons.length > 0 || (!hideSaveButton && handleSubmit) || !hideCloseButton) && (
				<Modal.Footer>
					{customButtons.length > 0 && (
						<div className="modal-footer-custom">
							{customButtons.map((buttonProps, index) => (
								<span key={index}>
									{renderButton({
										...buttonProps,
										text: t(buttonProps.text),
									})}
								</span>
							))}
						</div>
					)}

					<div className="modal-footer-actions">
						{/* Cancel button */}
						{!hideCloseButton && (
							<Button
								variant="secondary"
								onClick={handleClose}
								className="modal-cancel-btn"
								disabled={disabled}
							>
								{t("common.cancel")}
							</Button>
						)}

						{/* Primary save button */}
						{!hideSaveButton && handleSubmit && (
							<Button
								className="modal-save-btn"
								onClick={handleSubmit}
								disabled={disabled || submitDisabled}
							>
								{disabled && (
									<Spinner
										as="span"
										animation="border"
										size="sm"
										role="status"
										aria-hidden="true"
										className="me-2"
									/>
								)}
								{t(saveText || "common.save")}
							</Button>
						)}
					</div>
				</Modal.Footer>
			)}
			{footer && <Modal.Footer>{footer}</Modal.Footer>}
		</Modal>
	);
}
export default ModalAtom;
